import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  Form, Input, Button, Progress,
} from 'framework-ui-components';
import ErroreIcon from 'framework-ui-components/Components/Icons/ErroreIcon';
import classNames from 'classnames';
import Actions from '../Actions';
import { addParamToUrl, base64Decode } from '../Utils';
import AccountIcon from '../Icons/AccountIcon';

const EffettuaLogin = ({
  applicationStyle, informazioniAddizionaliLogin, referer, client, credenziali,
}) => {
  const refPassword = useRef();
  const refCf = useRef();
  const refBtnAccedi = useRef();
  const refLoginForm = useRef();

  const [formState, setFormState] = useState({});
  const [fillAnimationVerificata, setFillAnimationVerificata] = useState(false);
  const [firstAutofill, setFirstAutofill] = useState(true);
  const [utenteBloccato, setUtenteBloccato] = useState(false);
  const [erroreLogin, setErroreLogin] = useState(false);
  const [skipDisableBtn, setSkipDisableBtn] = useState(false);
  const [errorAnimation, setErrorAnimation] = useState(false);
  const [inFaseDiLogin, setInFaseDiLogin] = useState(false);

  const autoLogin = useCallback((username, password) => {
    login(
      username,
      password,
      (res) => {
        if (res.Success) {
          window.location = referer;
        }
      },
      (res) => {
        setErroreLogin(true);
      },
    );
  }, [referer]);

  const forceUpdate = useCallback(() => {}, []);

  const onAutoFillStart = useCallback((el) => {
    el.parentNode.parentNode.classList.add('input--valid');
    el.parentNode.parentNode.classList.remove('input--empty');
    setSkipDisableBtn(true);
    forceUpdate();
    if (firstAutofill) {
      refBtnAccedi.current.refButton.focus();
      setFirstAutofill(false);
    }
  }, [firstAutofill, forceUpdate]);

  const onAutoFillCancel = useCallback((el) => {
    el.parentNode.parentNode.classList.remove('input--valid');
    el.parentNode.parentNode.classList.add('input--empty');
    setSkipDisableBtn(false);
    forceUpdate();
  }, [forceUpdate]);

  const onAnimationStart = useCallback(({ target, animationName }) => {
    switch (animationName) {
      case 'onAutoFillStart':
        return onAutoFillStart(target);
      case 'onAutoFillCancel':
        return onAutoFillCancel(target);
      default:
        return () => {};
    }
  }, [onAutoFillCancel, onAutoFillStart]);

  const verificaAutofill = useCallback(() => {
    if (fillAnimationVerificata) {
      return;
    }
    setFillAnimationVerificata(true);
    setFirstAutofill(false);

    const focusTarget = (utenteBloccato ? refPassword : refCf);

    const focusUnformattedFieldTarget = focusTarget.current ? focusTarget.current.unformattedField.current : null;

    if (!focusUnformattedFieldTarget) return;

    try {
      if (document.querySelectorAll('input:-webkit-autofill').length === 0) {
        focusUnformattedFieldTarget.focus();
      }
    } catch (err) {
      // Internet explorer esplode per un errore di sintassi
      focusUnformattedFieldTarget.focus();
    }
  }, [fillAnimationVerificata, utenteBloccato]);

  const onAnimationEnd = useCallback(({ target, animationName }) => {
    switch (animationName) {
      case 'onAutoFillStart':
        return verificaAutofill();
      default:
        return () => {};
    }
  }, [verificaAutofill]);

  useEffect(() => {
    [refCf, refPassword].forEach((ref) => {
      if (ref.current && ref.current.unformattedField.current) {
        ref.current.unformattedField.current.addEventListener('animationstart', onAnimationStart, false);
        ref.current.unformattedField.current.addEventListener('animationend', onAnimationEnd, false);
      }
    });

    if (credenziali) {
      const credenzialiBase64 = base64Decode(credenziali);
      const username = credenzialiBase64.substring(0, credenzialiBase64.indexOf(':')).trim();
      const password = credenzialiBase64.substring(credenzialiBase64.indexOf(':') + 1, credenzialiBase64.length).trim();
      if (password.length === 0) {
        setFormState({ CodiceFiscale: username });
        setUtenteBloccato(true);
      } else {
        autoLogin(username, password);
      }
    }

    setTimeout(verificaAutofill, 500);
  }, [autoLogin, credenziali, onAnimationEnd, onAnimationStart, verificaAutofill]);

  const effettuaLogin = () => {
    if (!refLoginForm.current.isValid) {
      return null;
    }

    return login(
      formState.CodiceFiscale,
      formState.Password,
      () => autenticazioneCompletata(),
      () => {
        setErrorAnimation(true);
        setErroreLogin(true);
        setTimeout(() => {
          setErrorAnimation(false);
        }, 500);
      },
    );
  };

  const login = (username, password, success, error) => {
    setErroreLogin(false);
    return Actions.Login(username, password).then(() => {
      setInFaseDiLogin(true);
      success();
    }, error);
  };

  const autenticazioneCompletata = () => {
    try {
      const parser = document.createElement('a');
      parser.href = referer;

      if (parser.hostname === 'local.servizicgn.it') {
        document.cookie = `webserverport=${parser.port};Domain=.cgn.it;path=/;Secure`;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
    window.location = referer;
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      effettuaLogin();
    }
  };

  const generaLinkRecuperaPassword = useMemo(() => {
    let link = '/RecuperaPassword';

    if (client) {
      link = addParamToUrl(link, `client=${client}`);
    }
    if (referer) {
      link = addParamToUrl(link, `referer=${encodeURIComponent(referer)}`);
    }

    return link;
  }, [client, referer]);

  const classes = classNames('accounts__container accounts__effettuaLogin', { animated: errorAnimation });
  const progressClasses = classNames('accounts__progress', { show: inFaseDiLogin });
  const opacityClasses = classNames('accounts__opacityLayer', { show: inFaseDiLogin });
  return (
    <div className="account__col">
      <div className="accounts__wrapper">
        <div className={classes}>
          <div className="accounts__header">
            <Progress indeterminate className={progressClasses} />
            {applicationStyle.logo || <AccountIcon />}
          </div>
          <Form ref={refLoginForm} onChange={((x) => setFormState((old) => ({ ...old, ...x })))} stato={formState}>
            <h3>Accedi</h3>
            {applicationStyle.registratiTop}
            <Input
              title="Codice fiscale"
              name="CodiceFiscale"
              validations={['required']}
              spellCheck="false"
              ref={refCf}
              readOnly={utenteBloccato}
              onKeyPress={handleKeyPress}
              actions={informazioniAddizionaliLogin}
            />
            <Input
              title="Password"
              name="Password"
              validations={['required']}
              type="password"
              ref={refPassword}
              onKeyPress={handleKeyPress}
            />
            <div className="accountsForm__actions">
              <Link to={generaLinkRecuperaPassword}>Hai dimenticato la password?</Link><br />
              <div className="login__btnContainer">
                <Button
                  primary
                  className="effettuaLogin__button"
                  disabled={!skipDisableBtn && (!refLoginForm.current || !refLoginForm.current.isValid)}
                  onClick={effettuaLogin}
                  ref={refBtnAccedi}
                >
                  Accedi
                </Button>
                {applicationStyle.registrati}
                {erroreLogin && (
                  <div className="effettuaLogin__errore">
                    <ErroreIcon /><span>Codice fiscale o password errati</span>
                  </div>
                )}
              </div>
            </div>
          </Form>
          <div className={opacityClasses} />
        </div>
      </div>
    </div>
  );
};

EffettuaLogin.defaultProps = {
  informazioniAddizionaliLogin: null,
  referer: null,
  client: null,
  credenziali: null,
};

EffettuaLogin.propTypes = {
  applicationStyle: PropTypes.shape({
    logo: PropTypes.element,
    registrati: PropTypes.element,
    registratiTop: PropTypes.element,
  }).isRequired,
  informazioniAddizionaliLogin: PropTypes.element,
  referer: PropTypes.string,
  client: PropTypes.string,
  credenziali: PropTypes.string,
};

export default EffettuaLogin;
